import { gsap } from 'gsap'
import barba from '@barba/core'
import barbaRouter from '@barba/router'
import {
  debounce,
  getViewportWidth,
  urlIsFile,
  nodesToArray,
  setLocalStorageItem,
  getStyleProperty,
} from '../utilities'
import {
  reducedMotion,
  transitionTimingMilliseconds,
} from '../utilities/constants'

const saveScrollSelector = '.js-saveScroll'
const extraScrollSelector = '.js-extraScroll'
const storageName = 'saqmi-scroll'
const backButtonSelector = '.js-backButton'
const inTransitionClass = 'in-pageTransition'

export default class {
  constructor(el, options) {
    this.options = options

    let viewportWidth = getViewportWidth()
    const transitionTimingSeconds = reducedMotion
      ? 0
      : transitionTimingMilliseconds / 1000
    const easing = 'sine.inOut'
    const routes = [
      {
        path: ['/', '/\\?s=:search?', '/tag/:tag', '/:id'],
        name: 'page',
      },
      {
        path: '/:category/page/:number',
        name: 'paged_page',
      },
      {
        path: '/:category/:id',
        name: 'post',
      },
    ]

    barba.use(barbaRouter, {
      routes,
    })

    function setupContainer(event) {
      const container = event ? event.next.container : el

      if (
        barba.history &&
        barba.history.previous &&
        barba.history.previous.url
      ) {
        const backButtons = container.querySelectorAll(backButtonSelector)

        if (backButtons) {
          nodesToArray(backButtons).forEach(function (backButton) {
            backButton.setAttribute('href', barba.history.previous.url)
          })
        }
      }

      const scrollLinks = container.querySelectorAll('a[href^="#"]')

      if (scrollLinks) {
        nodesToArray(scrollLinks).forEach(function (linkEl) {
          linkEl.addEventListener('click', function (e) {
            e.preventDefault()

            const targetEl = container.querySelector(
              linkEl.getAttribute('href')
            )

            targetEl.scrollIntoView({
              behavior: 'smooth',
            })
          })
        })
      }
    }

    barba.hooks.before(function (e) {
      el.classList.toggle(inTransitionClass, true)

      setTimeout(function () {
        setupContainer(e)
      }, transitionTimingMilliseconds)
    })

    barba.hooks.after(function () {
      el.classList.toggle(inTransitionClass, false)
    })

    setupContainer()

    // Save scroll position

    function saveScrollPosition(container) {
      container = container || document.querySelector(saveScrollSelector)

      if (container) {
        const pageSlug = container.getAttribute('data-slug')
        const scrollPosition = Math.floor(container.scrollTop)
        let savedScrollData = window.localStorage.getItem(storageName)

        if (savedScrollData) {
          savedScrollData = JSON.parse(savedScrollData)
        }

        if (Array.isArray(savedScrollData) === false) {
          window.localStorage.removeItem(storageName)
          savedScrollData = []
        }

        const now = new Date()
        const newScrollData = [
          ...savedScrollData.filter(function (page, index) {
            return (
              index > savedScrollData.length - 2 &&
              page.slug !== pageSlug &&
              page.exp > now.getTime()
            )
          }),
          {
            slug: pageSlug,
            pos: scrollPosition,
            exp: now.getTime() + 600000, // 3 minutes
          },
        ]

        setLocalStorageItem(storageName, JSON.stringify(newScrollData))
      }
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        saveScrollPosition()
      }
    })

    document.addEventListener('unload', function () {
      saveScrollPosition()
    })

    document.addEventListener('beforeunload', function () {
      saveScrollPosition()
    })

    document.addEventListener('pagehide', function () {
      saveScrollPosition()
    })

    barba.hooks.before(function (data) {
      saveScrollPosition(
        data.current.container.querySelector(saveScrollSelector)
      )
    })

    // Use saved scroll position
    barba.hooks.beforeEnter(function (data) {
      const nextScrollContainer =
        data.next.container.querySelector(saveScrollSelector)

      if (nextScrollContainer) {
        const pageSlug = nextScrollContainer.getAttribute('data-slug')
        let savedScrollData = window.localStorage.getItem(storageName)

        if (savedScrollData && pageSlug) {
          savedScrollData = JSON.parse(savedScrollData)

          if (Array.isArray(savedScrollData) === false) {
            window.localStorage.removeItem(storageName)
            return
          }

          const savedPagePosition = savedScrollData.find(function (page) {
            return page.slug === pageSlug
          })
          const now = new Date()

          if (savedPagePosition && savedPagePosition.exp > now.getTime()) {
            const extraScrollEl =
              nextScrollContainer.querySelector(extraScrollSelector)
            let extraScroll = 0

            if (extraScrollEl) {
              extraScroll =
                extraScrollEl.offsetHeight +
                parseInt(window.getComputedStyle(extraScrollEl)['margin-top'])
            }

            nextScrollContainer.scrollTop = savedPagePosition.pos + extraScroll
          }
        }
      }

      const pageColor = getStyleProperty('--page-color', data.next.container)

      if (pageColor?.length) {
        document.documentElement.style.backgroundColor = pageColor
      }
    })

    barba.init({
      debug: window.location.hostname.includes('test'),
      timeout: 10000,
      cacheIgnore: false,
      preventRunning: true,
      prevent: function ({ el, href, event }) {
        return (
          el.getAttribute('data-barba-prevent') ||
          el.getAttribute('href').startsWith('#') ||
          (href && (urlIsFile(href) || href.includes('wp-admin')))
        )
      },
      transitions: [
        {
          name: 'page-to-post',
          sync: true,
          from: {
            route: ['page', 'paged_page'],
          },
          to: {
            route: 'post',
          },
          enter(data) {
            const done = this.async()

            const nextPageType = data.next.container.getAttribute('data-type')
            const currentPageSlug =
              data.current.container.getAttribute('data-slug')
            const isGoingFromParentToChild = data.next.url.path.startsWith(
              '/' + currentPageSlug
            )
            const isChildPage = nextPageType === 'child'
            const isPostPage = nextPageType === 'post'
            const isFullPage =
              isPostPage ||
              (!isGoingFromParentToChild && isChildPage) ||
              (isGoingFromParentToChild && !isChildPage)
            const largeViewport = viewportWidth > 800

            gsap.fromTo(
              data.next.container,
              {
                x: !largeViewport || isFullPage ? '100%' : '50%',
              },
              {
                x: '0%',
                duration:
                  largeViewport && isFullPage
                    ? transitionTimingSeconds * 2
                    : transitionTimingSeconds,
                ease: easing,
                onComplete: function () {
                  window.setTimeout(function () {
                    gsap.set(data.next.container, {
                      clearProps: 'transform',
                    })
                  }, 100)

                  done()
                },
              }
            )
          },
          beforeEnter({ next }) {
            next.container.style.zIndex = 4
          },
          after({ next }) {
            next.container.style.zIndex = ''
          },
        },
        {
          name: 'post-to-page',
          sync: true,
          from: {
            route: 'post',
          },
          to: {
            route: ['page', 'paged_page'],
          },
          leave(data) {
            const done = this.async()
            const largeViewport = viewportWidth > 800

            gsap.fromTo(
              data.current.container,
              {
                x: '0%',
              },
              {
                x: '100%',
                ease: easing,
                duration: largeViewport
                  ? transitionTimingSeconds * 2
                  : transitionTimingSeconds,
                onComplete: function () {
                  window.setTimeout(function () {
                    gsap.set(data.current.container, {
                      clearProps: 'transform',
                    })

                    done()
                  }, 100)
                },
              }
            )
          },
        },
        {
          name: 'post-to-post',
          sync: true,
          from: {
            route: 'post',
          },
          to: {
            route: 'post',
          },
          beforeEnter({ next }) {
            next.container.style.zIndex = 6
          },
          enter(data) {
            const done = this.async()
            const largeViewport = viewportWidth > 800

            gsap.fromTo(
              data.next.container,
              {
                x: '100%',
              },
              {
                x: '0%',
                ease: easing,
                duration: largeViewport
                  ? transitionTimingSeconds * 2
                  : transitionTimingSeconds,
                onComplete: function () {
                  window.setTimeout(function () {
                    gsap.set(data.next.container, {
                      clearProps: 'transform',
                    })
                  }, 100)

                  done()
                },
              }
            )
          },
          after({ next }) {
            next.container.style.zIndex = ''
          },
        },
        {
          name: 'page-to-page',
          sync: true,
          from: {
            route: ['page', 'paged_page'],
          },
          to: {
            route: ['page', 'paged_page'],
          },
          enter(data) {
            const done = this.async()
            const largeViewport = viewportWidth > 800

            gsap.fromTo(
              data.next.container,
              {
                x: '100%',
              },
              {
                x: largeViewport ? '50%' : '0%',
                ease: easing,
                duration: transitionTimingSeconds,
                onComplete: function () {
                  window.setTimeout(function () {
                    gsap.set(data.next.container, {
                      clearProps: 'transform',
                    })
                  }, 100)

                  done()
                },
              }
            )
          },
          beforeEnter({ next }) {
            next.container.style.zIndex = 3
          },
          after({ next }) {
            next.container.style.zIndex = ''
          },
        },
      ],
    })

    window.addEventListener(
      'resize',
      debounce(function () {
        viewportWidth = getViewportWidth()
      }, 300)
    )
  }
}
